import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';
import type { LandingSection } from '@/hooks/useLandingSections';
import { ImageUpload } from './ImageUpload';

interface SectionEditorProps {
  section: LandingSection | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: string, content: Record<string, unknown>) => Promise<void>;
}

export function SectionEditor({ section, isOpen, onClose, onSave }: SectionEditorProps) {
  const [content, setContent] = useState<Record<string, unknown>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (section) {
      setContent(section.content || {});
    }
  }, [section]);

  const handleSave = async () => {
    if (!section) return;
    
    try {
      setIsSaving(true);
      await onSave(section.id, content);
      toast.success('Sektion gespeichert');
      onClose();
    } catch (error) {
      toast.error('Fehler beim Speichern');
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (key: string, value: unknown) => {
    setContent(prev => ({ ...prev, [key]: value }));
  };

  const updateNestedField = (parentKey: string, index: number, field: string, value: unknown) => {
    setContent(prev => {
      const items = [...(prev[parentKey] as Array<Record<string, unknown>> || [])];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, [parentKey]: items };
    });
  };

  const addItem = (key: string, template: Record<string, unknown>) => {
    setContent(prev => ({
      ...prev,
      [key]: [...(prev[key] as Array<Record<string, unknown>> || []), template],
    }));
  };

  const removeItem = (key: string, index: number) => {
    setContent(prev => ({
      ...prev,
      [key]: (prev[key] as Array<Record<string, unknown>>).filter((_, i) => i !== index),
    }));
  };

  if (!section) return null;

  const renderEditor = () => {
    switch (section.slug) {
      case 'modern_hero':
        return renderHeroEditor();
      case 'christmas_device_setup':
        return renderChristmasEditor();
      case 'why':
        return renderWhyEditor();
      case 'services':
        return renderServicesEditor();
      case 'times':
        return renderTimesEditor();
      case 'prices':
        return renderPricesEditor();
      case 'faq':
        return renderFaqEditor();
      case 'gift_voucher_banner':
        return renderGiftVoucherEditor();
      case 'contact':
        return renderContactEditor();
      case 'chatbot_product':
        return renderChatbotProductEditor();
      case 'trust_logos':
        return renderTrustLogosEditor();
      case 'footer':
        return renderFooterEditor();
      default:
        return renderGenericEditor();
    }
  };

  const renderHeroEditor = () => (
    <div className="space-y-4">
      <div>
        <Label>Badge</Label>
        <Input 
          value={content.badge as string || ''} 
          onChange={(e) => updateField('badge', e.target.value)} 
        />
      </div>
      <div>
        <Label>Titel</Label>
        <Input 
          value={content.title as string || ''} 
          onChange={(e) => updateField('title', e.target.value)} 
        />
      </div>
      <div>
        <Label>Titel Highlight</Label>
        <Input 
          value={content.titleHighlight as string || ''} 
          onChange={(e) => updateField('titleHighlight', e.target.value)} 
        />
      </div>
      <div>
        <Label>Untertitel</Label>
        <Textarea 
          value={content.subtitle as string || ''} 
          onChange={(e) => updateField('subtitle', e.target.value)} 
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Primärer CTA</Label>
          <Input 
            value={content.ctaPrimary as string || ''} 
            onChange={(e) => updateField('ctaPrimary', e.target.value)} 
          />
        </div>
        <div>
          <Label>Sekundärer CTA</Label>
          <Input 
            value={content.ctaSecondary as string || ''} 
            onChange={(e) => updateField('ctaSecondary', e.target.value)} 
          />
        </div>
      </div>
      <div>
        <Label className="mb-2 block">Statistiken</Label>
        {(content.stats as Array<{ value: string; label: string }> || []).map((stat, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <Input 
              placeholder="Wert" 
              value={stat.value || ''} 
              onChange={(e) => updateNestedField('stats', i, 'value', e.target.value)} 
            />
            <Input 
              placeholder="Label" 
              value={stat.label || ''} 
              onChange={(e) => updateNestedField('stats', i, 'label', e.target.value)} 
            />
            <Button variant="ghost" size="icon" onClick={() => removeItem('stats', i)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => addItem('stats', { value: '', label: '' })}>
          <Plus className="h-4 w-4 mr-1" /> Statistik hinzufügen
        </Button>
      </div>
    </div>
  );

  const renderChristmasEditor = () => (
    <div className="space-y-4">
      <div>
        <Label>Badge</Label>
        <Input 
          value={content.badge as string || ''} 
          onChange={(e) => updateField('badge', e.target.value)} 
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Titel</Label>
          <Input 
            value={content.title as string || ''} 
            onChange={(e) => updateField('title', e.target.value)} 
          />
        </div>
        <div>
          <Label>Titel Highlight</Label>
          <Input 
            value={content.titleHighlight as string || ''} 
            onChange={(e) => updateField('titleHighlight', e.target.value)} 
          />
        </div>
      </div>
      <div>
        <Label>Untertitel</Label>
        <Textarea 
          value={content.subtitle as string || ''} 
          onChange={(e) => updateField('subtitle', e.target.value)} 
        />
      </div>
      <div>
        <Label>Preis-Info</Label>
        <Input 
          value={content.pricing as string || ''} 
          onChange={(e) => updateField('pricing', e.target.value)} 
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Primärer CTA</Label>
          <Input 
            value={content.ctaPrimary as string || ''} 
            onChange={(e) => updateField('ctaPrimary', e.target.value)} 
          />
        </div>
        <div>
          <Label>Sekundärer CTA</Label>
          <Input 
            value={content.ctaSecondary as string || ''} 
            onChange={(e) => updateField('ctaSecondary', e.target.value)} 
          />
        </div>
      </div>
      <div>
        <Label className="mb-2 block">Karten</Label>
        {(content.cards as Array<{ emoji: string; title: string; items: string[] }> || []).map((card, i) => (
          <div key={i} className="border p-3 rounded mb-3">
            <div className="flex gap-2 mb-2">
              <Input 
                placeholder="Emoji" 
                value={card.emoji || ''} 
                onChange={(e) => updateNestedField('cards', i, 'emoji', e.target.value)}
                className="w-20"
              />
              <Input 
                placeholder="Titel" 
                value={card.title || ''} 
                onChange={(e) => updateNestedField('cards', i, 'title', e.target.value)} 
              />
              <Button variant="ghost" size="icon" onClick={() => removeItem('cards', i)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <Textarea 
              placeholder="Items (eine pro Zeile)"
              value={(card.items || []).join('\n')}
              onChange={(e) => updateNestedField('cards', i, 'items', e.target.value.split('\n'))}
              rows={3}
            />
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => addItem('cards', { emoji: '', title: '', items: [] })}>
          <Plus className="h-4 w-4 mr-1" /> Karte hinzufügen
        </Button>
      </div>
    </div>
  );

  const renderWhyEditor = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Titel</Label>
          <Input 
            value={content.title as string || ''} 
            onChange={(e) => updateField('title', e.target.value)} 
          />
        </div>
        <div>
          <Label>Titel Highlight</Label>
          <Input 
            value={content.titleHighlight as string || ''} 
            onChange={(e) => updateField('titleHighlight', e.target.value)} 
          />
        </div>
      </div>
      <div>
        <Label>Untertitel</Label>
        <Input 
          value={content.subtitle as string || ''} 
          onChange={(e) => updateField('subtitle', e.target.value)} 
        />
      </div>
      <div>
        <Label className="mb-2 block">Vorteile</Label>
        {(content.items as Array<{ icon: string; title: string; description: string }> || []).map((item, i) => (
          <div key={i} className="border p-3 rounded mb-2">
            <div className="flex gap-2 mb-2">
              <Input 
                placeholder="Icon (z.B. MapPin)" 
                value={item.icon || ''} 
                onChange={(e) => updateNestedField('items', i, 'icon', e.target.value)}
                className="w-32"
              />
              <Input 
                placeholder="Titel" 
                value={item.title || ''} 
                onChange={(e) => updateNestedField('items', i, 'title', e.target.value)} 
              />
              <Button variant="ghost" size="icon" onClick={() => removeItem('items', i)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <Textarea 
              placeholder="Beschreibung"
              value={item.description || ''}
              onChange={(e) => updateNestedField('items', i, 'description', e.target.value)}
              rows={2}
            />
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => addItem('items', { icon: '', title: '', description: '' })}>
          <Plus className="h-4 w-4 mr-1" /> Vorteil hinzufügen
        </Button>
      </div>
    </div>
  );

  const renderServicesEditor = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Titel</Label>
          <Input 
            value={content.title as string || ''} 
            onChange={(e) => updateField('title', e.target.value)} 
          />
        </div>
        <div>
          <Label>Titel Highlight</Label>
          <Input 
            value={content.titleHighlight as string || ''} 
            onChange={(e) => updateField('titleHighlight', e.target.value)} 
          />
        </div>
      </div>
      <div>
        <Label>Untertitel</Label>
        <Input 
          value={content.subtitle as string || ''} 
          onChange={(e) => updateField('subtitle', e.target.value)} 
        />
      </div>
      <div>
        <Label className="mb-2 block">Service-Karten</Label>
        {(content.cards as Array<{ badge?: string; title: string; description: string; items: string[]; image?: string; imageAlt?: string }> || []).map((card, i) => (
          <div key={i} className="border p-3 rounded mb-3">
            <div className="flex gap-2 mb-2">
              <Input 
                placeholder="Badge (z.B. Für Unternehmen)" 
                value={card.badge || ''} 
                onChange={(e) => updateNestedField('cards', i, 'badge', e.target.value)} 
              />
              <Button variant="ghost" size="icon" onClick={() => removeItem('cards', i)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <Input 
              placeholder="Titel"
              value={card.title || ''}
              onChange={(e) => updateNestedField('cards', i, 'title', e.target.value)}
              className="mb-2"
            />
            <Textarea 
              placeholder="Beschreibung"
              value={card.description || ''}
              onChange={(e) => updateNestedField('cards', i, 'description', e.target.value)}
              rows={2}
              className="mb-2"
            />
            <Textarea 
              placeholder="Leistungen (eine pro Zeile)"
              value={(card.items || []).join('\n')}
              onChange={(e) => updateNestedField('cards', i, 'items', e.target.value.split('\n'))}
              rows={4}
              className="mb-2"
            />
            <Label className="text-xs text-muted-foreground mb-1">Bild</Label>
            <ImageUpload
              value={card.image || ''}
              onChange={(url) => updateNestedField('cards', i, 'image', url)}
              placeholder="Bild-URL"
            />
            <Input 
              placeholder="Bild Alt-Text"
              value={card.imageAlt || ''}
              onChange={(e) => updateNestedField('cards', i, 'imageAlt', e.target.value)}
              className="mt-2"
            />
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => addItem('cards', { badge: '', title: '', description: '', items: [], image: '', imageAlt: '' })}>
          <Plus className="h-4 w-4 mr-1" /> Service-Karte hinzufügen
        </Button>
      </div>
    </div>
  );

  const renderTimesEditor = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Titel</Label>
          <Input 
            value={content.title as string || ''} 
            onChange={(e) => updateField('title', e.target.value)} 
          />
        </div>
        <div>
          <Label>Titel Highlight</Label>
          <Input 
            value={content.titleHighlight as string || ''} 
            onChange={(e) => updateField('titleHighlight', e.target.value)} 
          />
        </div>
      </div>
      <div>
        <Label className="mb-2 block">Öffnungszeiten</Label>
        {(content.slots as Array<{ day: string; time: string }> || []).map((slot, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <Input 
              placeholder="Tag" 
              value={slot.day || ''} 
              onChange={(e) => updateNestedField('slots', i, 'day', e.target.value)} 
            />
            <Input 
              placeholder="Zeit" 
              value={slot.time || ''} 
              onChange={(e) => updateNestedField('slots', i, 'time', e.target.value)} 
            />
            <Button variant="ghost" size="icon" onClick={() => removeItem('slots', i)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => addItem('slots', { day: '', time: '' })}>
          <Plus className="h-4 w-4 mr-1" /> Zeitslot hinzufügen
        </Button>
      </div>
      <div>
        <Label>Hinweis</Label>
        <Input 
          value={content.note as string || ''} 
          onChange={(e) => updateField('note', e.target.value)} 
        />
      </div>
    </div>
  );

  const renderPricesEditor = () => {
    const categories = (content.categories as Array<{
      icon?: string;
      title: string;
      subtitle?: string;
      intro?: string;
      packages: Array<{
        name: string;
        description: string;
        price: string;
        priceUnit?: string;
        features: string[];
        recommended?: boolean;
      }>;
    }>) || [];

    const updateCategory = (catIdx: number, field: string, value: unknown) => {
      const newCategories = [...categories];
      newCategories[catIdx] = { ...newCategories[catIdx], [field]: value };
      updateField('categories', newCategories);
    };

    const updatePackage = (catIdx: number, pkgIdx: number, field: string, value: unknown) => {
      const newCategories = [...categories];
      const packages = [...newCategories[catIdx].packages];
      packages[pkgIdx] = { ...packages[pkgIdx], [field]: value };
      newCategories[catIdx] = { ...newCategories[catIdx], packages };
      updateField('categories', newCategories);
    };

    const addPackage = (catIdx: number) => {
      const newCategories = [...categories];
      newCategories[catIdx].packages.push({
        name: '',
        description: '',
        price: '',
        priceUnit: 'Monat',
        features: [],
        recommended: false,
      });
      updateField('categories', newCategories);
    };

    const removePackage = (catIdx: number, pkgIdx: number) => {
      const newCategories = [...categories];
      newCategories[catIdx].packages = newCategories[catIdx].packages.filter((_, i) => i !== pkgIdx);
      updateField('categories', newCategories);
    };

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Titel</Label>
            <Input 
              value={content.title as string || ''} 
              onChange={(e) => updateField('title', e.target.value)} 
            />
          </div>
          <div>
            <Label>Titel Highlight</Label>
            <Input 
              value={content.titleHighlight as string || ''} 
              onChange={(e) => updateField('titleHighlight', e.target.value)} 
            />
          </div>
        </div>
        <div>
          <Label>Untertitel</Label>
          <Input 
            value={content.subtitle as string || ''} 
            onChange={(e) => updateField('subtitle', e.target.value)} 
          />
        </div>

        {categories.map((cat, catIdx) => (
          <div key={catIdx} className="border-2 border-primary/20 p-4 rounded-lg">
            <h4 className="font-bold mb-3 text-primary">Kategorie: {cat.title || `Kategorie ${catIdx + 1}`}</h4>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <Input 
                placeholder="Icon (Laptop2, Users, Shield, Zap)"
                value={cat.icon || ''}
                onChange={(e) => updateCategory(catIdx, 'icon', e.target.value)}
              />
              <Input 
                placeholder="Kategorie-Titel"
                value={cat.title || ''}
                onChange={(e) => updateCategory(catIdx, 'title', e.target.value)}
              />
            </div>
            <Input 
              placeholder="Untertitel"
              value={cat.subtitle || ''}
              onChange={(e) => updateCategory(catIdx, 'subtitle', e.target.value)}
              className="mb-2"
            />
            <Textarea 
              placeholder="Einleitungstext"
              value={cat.intro || ''}
              onChange={(e) => updateCategory(catIdx, 'intro', e.target.value)}
              rows={2}
              className="mb-3"
            />

            <Label className="mb-2 block text-sm">Pakete in dieser Kategorie:</Label>
            {cat.packages.map((pkg, pkgIdx) => (
              <div key={pkgIdx} className="border p-3 rounded mb-2 bg-muted/30">
                <div className="flex gap-2 mb-2">
                  <Input 
                    placeholder="Paket-Name" 
                    value={pkg.name || ''} 
                    onChange={(e) => updatePackage(catIdx, pkgIdx, 'name', e.target.value)} 
                  />
                  <Input 
                    placeholder="Preis" 
                    value={pkg.price || ''} 
                    onChange={(e) => updatePackage(catIdx, pkgIdx, 'price', e.target.value)}
                    className="w-28"
                  />
                  <Input 
                    placeholder="Einheit" 
                    value={pkg.priceUnit || ''} 
                    onChange={(e) => updatePackage(catIdx, pkgIdx, 'priceUnit', e.target.value)}
                    className="w-24"
                  />
                  <Button variant="ghost" size="icon" onClick={() => removePackage(catIdx, pkgIdx)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <Input 
                  placeholder="Beschreibung"
                  value={pkg.description || ''}
                  onChange={(e) => updatePackage(catIdx, pkgIdx, 'description', e.target.value)}
                  className="mb-2"
                />
                <Textarea 
                  placeholder="Features (eine pro Zeile)"
                  value={(pkg.features || []).join('\n')}
                  onChange={(e) => updatePackage(catIdx, pkgIdx, 'features', e.target.value.split('\n'))}
                  rows={3}
                  className="mb-2"
                />
                <label className="flex items-center gap-2 text-sm">
                  <input 
                    type="checkbox"
                    checked={pkg.recommended || false}
                    onChange={(e) => updatePackage(catIdx, pkgIdx, 'recommended', e.target.checked)}
                  />
                  Empfohlen (hervorgehoben anzeigen)
                </label>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => addPackage(catIdx)}>
              <Plus className="h-4 w-4 mr-1" /> Paket hinzufügen
            </Button>
          </div>
        ))}
      </div>
    );
  };

  const renderFaqEditor = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Titel</Label>
          <Input 
            value={content.title as string || ''} 
            onChange={(e) => updateField('title', e.target.value)} 
          />
        </div>
        <div>
          <Label>Titel Highlight</Label>
          <Input 
            value={content.titleHighlight as string || ''} 
            onChange={(e) => updateField('titleHighlight', e.target.value)} 
          />
        </div>
      </div>
      <div>
        <Label className="mb-2 block">Fragen & Antworten</Label>
        {(content.items as Array<{ question: string; answer: string }> || []).map((item, i) => (
          <div key={i} className="border p-3 rounded mb-2">
            <div className="flex gap-2 mb-2">
              <Input 
                placeholder="Frage" 
                value={item.question || ''} 
                onChange={(e) => updateNestedField('items', i, 'question', e.target.value)} 
              />
              <Button variant="ghost" size="icon" onClick={() => removeItem('items', i)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <Textarea 
              placeholder="Antwort"
              value={item.answer || ''}
              onChange={(e) => updateNestedField('items', i, 'answer', e.target.value)}
              rows={3}
            />
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => addItem('items', { question: '', answer: '' })}>
          <Plus className="h-4 w-4 mr-1" /> FAQ hinzufügen
        </Button>
      </div>
    </div>
  );

  const renderGiftVoucherEditor = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Titel</Label>
          <Input 
            value={content.title as string || ''} 
            onChange={(e) => updateField('title', e.target.value)} 
          />
        </div>
        <div>
          <Label>Titel Highlight</Label>
          <Input 
            value={content.titleHighlight as string || ''} 
            onChange={(e) => updateField('titleHighlight', e.target.value)} 
          />
        </div>
      </div>
      <div>
        <Label>Untertitel</Label>
        <Input 
          value={content.subtitle as string || ''} 
          onChange={(e) => updateField('subtitle', e.target.value)} 
        />
      </div>
      <div>
        <Label>CTA Text</Label>
        <Input 
          value={content.ctaText as string || ''} 
          onChange={(e) => updateField('ctaText', e.target.value)} 
        />
      </div>
      <div>
        <Label className="mb-2 block">Features</Label>
        {(content.features as string[] || []).map((feature, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <Input 
              value={feature} 
              onChange={(e) => {
                const features = [...(content.features as string[] || [])];
                features[i] = e.target.value;
                updateField('features', features);
              }} 
            />
            <Button variant="ghost" size="icon" onClick={() => {
              const features = (content.features as string[] || []).filter((_, idx) => idx !== i);
              updateField('features', features);
            }}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => updateField('features', [...(content.features as string[] || []), ''])}>
          <Plus className="h-4 w-4 mr-1" /> Feature hinzufügen
        </Button>
      </div>
    </div>
  );

  const renderContactEditor = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Titel</Label>
          <Input 
            value={content.title as string || ''} 
            onChange={(e) => updateField('title', e.target.value)} 
          />
        </div>
        <div>
          <Label>Titel Highlight</Label>
          <Input 
            value={content.titleHighlight as string || ''} 
            onChange={(e) => updateField('titleHighlight', e.target.value)} 
          />
        </div>
      </div>
      <div>
        <Label>Untertitel</Label>
        <Input 
          value={content.subtitle as string || ''} 
          onChange={(e) => updateField('subtitle', e.target.value)} 
        />
      </div>
      <div>
        <Label>Telefon</Label>
        <Input 
          value={content.phone as string || ''} 
          onChange={(e) => updateField('phone', e.target.value)} 
        />
      </div>
      <div>
        <Label>E-Mail</Label>
        <Input 
          value={content.email as string || ''} 
          onChange={(e) => updateField('email', e.target.value)} 
        />
      </div>
      <div>
        <Label>Adresse</Label>
        <Input 
          value={content.address as string || ''} 
          onChange={(e) => updateField('address', e.target.value)} 
        />
      </div>
      <div>
        <Label>Formular-Titel</Label>
        <Input 
          value={content.formTitle as string || ''} 
          onChange={(e) => updateField('formTitle', e.target.value)} 
        />
      </div>
    </div>
  );

  const renderChatbotProductEditor = () => (
    <div className="space-y-4">
      <div>
        <Label>Badge</Label>
        <Input 
          value={content.badge as string || ''} 
          onChange={(e) => updateField('badge', e.target.value)} 
          placeholder="z.B. NEU: KI-Chatbot Service"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Titel</Label>
          <Input 
            value={content.title as string || ''} 
            onChange={(e) => updateField('title', e.target.value)} 
          />
        </div>
        <div>
          <Label>Titel Highlight</Label>
          <Input 
            value={content.titleHighlight as string || ''} 
            onChange={(e) => updateField('titleHighlight', e.target.value)} 
          />
        </div>
      </div>
      <div>
        <Label>Untertitel</Label>
        <Textarea 
          value={content.subtitle as string || ''} 
          onChange={(e) => updateField('subtitle', e.target.value)} 
          rows={3}
        />
      </div>
      <div>
        <Label className="mb-2 block">Features</Label>
        {(content.features as Array<{ icon: string; title: string; description: string }> || []).map((feature, i) => (
          <div key={i} className="border p-3 rounded mb-2">
            <div className="flex gap-2 mb-2">
              <Input 
                placeholder="Icon (Clock, Zap, Shield)" 
                value={feature.icon || ''} 
                onChange={(e) => updateNestedField('features', i, 'icon', e.target.value)}
                className="w-32"
              />
              <Input 
                placeholder="Titel" 
                value={feature.title || ''} 
                onChange={(e) => updateNestedField('features', i, 'title', e.target.value)} 
              />
              <Button variant="ghost" size="icon" onClick={() => removeItem('features', i)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <Textarea 
              placeholder="Beschreibung"
              value={feature.description || ''}
              onChange={(e) => updateNestedField('features', i, 'description', e.target.value)}
              rows={2}
            />
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => addItem('features', { icon: '', title: '', description: '' })}>
          <Plus className="h-4 w-4 mr-1" /> Feature hinzufügen
        </Button>
      </div>
      <div>
        <Label>CTA Box Titel</Label>
        <Input 
          value={content.ctaTitle as string || ''} 
          onChange={(e) => updateField('ctaTitle', e.target.value)} 
        />
      </div>
      <div>
        <Label>CTA Box Beschreibung</Label>
        <Textarea 
          value={content.ctaDescription as string || ''} 
          onChange={(e) => updateField('ctaDescription', e.target.value)} 
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Primärer CTA</Label>
          <Input 
            value={content.ctaPrimary as string || ''} 
            onChange={(e) => updateField('ctaPrimary', e.target.value)} 
          />
        </div>
        <div>
          <Label>Sekundärer CTA</Label>
          <Input 
            value={content.ctaSecondary as string || ''} 
            onChange={(e) => updateField('ctaSecondary', e.target.value)} 
          />
        </div>
      </div>
      <div>
        <Label className="mb-2 block">Statistiken</Label>
        {(content.stats as Array<{ value: string; label: string }> || []).map((stat, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <Input 
              placeholder="Wert (z.B. 0€)" 
              value={stat.value || ''} 
              onChange={(e) => updateNestedField('stats', i, 'value', e.target.value)} 
            />
            <Input 
              placeholder="Label" 
              value={stat.label || ''} 
              onChange={(e) => updateNestedField('stats', i, 'label', e.target.value)} 
            />
            <Button variant="ghost" size="icon" onClick={() => removeItem('stats', i)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => addItem('stats', { value: '', label: '' })}>
          <Plus className="h-4 w-4 mr-1" /> Statistik hinzufügen
        </Button>
      </div>
    </div>
  );

  const renderTrustLogosEditor = () => (
    <div className="space-y-4">
      <div>
        <Label>Titel</Label>
        <Input 
          value={content.title as string || ''} 
          onChange={(e) => updateField('title', e.target.value)} 
          placeholder="z.B. Vertrauensvolle Zusammenarbeit"
        />
      </div>
      <div>
        <Label className="mb-2 block">Logos/Partner</Label>
        {(content.logos as Array<{ name: string; imageUrl: string }> || []).map((logo, i) => (
          <div key={i} className="border p-3 rounded mb-2">
            <div className="flex gap-2 mb-2">
              <Input 
                placeholder="Name" 
                value={logo.name || ''} 
                onChange={(e) => updateNestedField('logos', i, 'name', e.target.value)} 
              />
              <Button variant="ghost" size="icon" onClick={() => removeItem('logos', i)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <ImageUpload
              value={logo.imageUrl || ''}
              onChange={(url) => updateNestedField('logos', i, 'imageUrl', url)}
              placeholder="Logo-URL"
            />
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => addItem('logos', { name: '', imageUrl: '' })}>
          <Plus className="h-4 w-4 mr-1" /> Logo hinzufügen
        </Button>
      </div>
    </div>
  );

  const renderFooterEditor = () => (
    <div className="space-y-4">
      <div>
        <Label>Firmenname</Label>
        <Input 
          value={content.companyName as string || ''} 
          onChange={(e) => updateField('companyName', e.target.value)} 
        />
      </div>
      <div>
        <Label>Beschreibung</Label>
        <Textarea 
          value={content.description as string || ''} 
          onChange={(e) => updateField('description', e.target.value)} 
          rows={3}
        />
      </div>
      <div>
        <Label>Telefon</Label>
        <Input 
          value={content.phone as string || ''} 
          onChange={(e) => updateField('phone', e.target.value)} 
        />
      </div>
      <div>
        <Label>E-Mail</Label>
        <Input 
          value={content.email as string || ''} 
          onChange={(e) => updateField('email', e.target.value)} 
        />
      </div>
      <div>
        <Label>Copyright Text</Label>
        <Input 
          value={content.copyright as string || ''} 
          onChange={(e) => updateField('copyright', e.target.value)} 
          placeholder="z.B. © 2024 Klartech Müritz. Alle Rechte vorbehalten."
        />
      </div>
    </div>
  );

  const renderGenericEditor = () => (
    <div className="space-y-4">
      <p className="text-muted-foreground">
        Für diese Sektion gibt es noch keinen spezialisierten Editor. 
        Du kannst die Inhalte als JSON bearbeiten:
      </p>
      <Textarea 
        value={JSON.stringify(content, null, 2)}
        onChange={(e) => {
          try {
            setContent(JSON.parse(e.target.value));
          } catch {
            // Invalid JSON, ignore
          }
        }}
        rows={15}
        className="font-mono text-sm"
      />
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{section.name} bearbeiten</DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          {renderEditor()}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Speichert...' : 'Speichern'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
